import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../../../config/ApiConfig';
import { formatCPF, unformatCPF, isValidCPF } from '../../utils/cpfUtils';
import CPFMaskInput from '../masks/CPFMaskInput';

function PessoaDependenteModal({
  show,
  onClose,
  onSaveSuccess,
  dependente = null,
  pessoa = null,
}) {
  const isEditing = !!dependente;

  const [formData, setFormData] = useState({
    cnpjAssociacao: '',
    idPessoa: '',
    cpf: '',
    nome: '',
    dataNasc: '',
    grauParentesco: '',
    sexo: '',
  });

  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [cpfError, setCpfError] = useState('');

  const token = localStorage.getItem('token');
  const cnpj = localStorage.getItem('cnpj');

  // Resetar formulário quando o modal abrir/fechar
  useEffect(() => {
    if (show) {
      if (isEditing && dependente) {
        setFormData({
          cnpjAssociacao: dependente.cnpjAssociacao || cnpj,
          idPessoa: dependente.idPessoa || pessoa?.id || '',
          id: dependente.id || '',
          cpf: dependente.cpf || '',
          nome: dependente.nome || '',
          dataNasc: dependente.dataNasc || '',
          grauParentesco: dependente.grauParentesco || '',
          sexo: dependente.sexo || '',
        });
      } else {
        setFormData({
          cnpjAssociacao: cnpj,
          idPessoa: pessoa?.id || '',
          cpf: '',
          nome: '',
          dataNasc: '',
          grauParentesco: '',
          sexo: '',
        });
      }
      setErrors({});
      setCpfError('');
    }
  }, [show, dependente, isEditing, cnpj, pessoa]);

  const handleInputChange = e => {
    const { name, value } = e.target;

    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));

    // Limpar erro do campo quando o usuário começar a digitar
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: '',
      }));
    }

    // Limpar erro específico do CPF
    if (name === 'cpf' && cpfError) {
      setCpfError('');
    }
  };
  const validateForm = () => {
    const newErrors = {};

    if (!formData.cpf.trim()) {
      newErrors.cpf = 'CPF é obrigatório';
    } else if (!isValidCPF(formData.cpf)) {
      newErrors.cpf = 'CPF inválido';
    }

    if (!formData.nome.trim()) {
      newErrors.nome = 'Nome é obrigatório';
    }
    if (!formData.dataNasc) {
      newErrors.dataNasc = 'Data de nascimento é obrigatória';
    }
    if (!formData.grauParentesco.trim()) {
      newErrors.grauParentesco = 'Grau de parentesco é obrigatório';
    }

    setErrors(newErrors);
    setCpfError(newErrors.cpf || '');
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async e => {
    e.preventDefault();

    if (!validateForm()) return;

    setIsLoading(true);

    try {
      const dataToSend = {
        ...formData,
        cnpjAssociacao: cnpj,
      };
      console.log('Dados enviados:', dataToSend); // Debug: Verificar os dados que estão sendo enviados
      let response;
      response = await axios.post(
        `${API_BASE_URL.PESSOA_DEPENDENTE}/save`,
        dataToSend,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      // Chama callback de sucesso
      if (onSaveSuccess) {
        onSaveSuccess(response.data, isEditing);
      }

      // Fecha o modal
      handleClose();
    } catch (error) {
      console.error('Erro ao salvar dependente:', error);

      // Tratar erros de validação do backend
      if (error.response?.status === 400) {
        setErrors({ submit: 'Dados inválidos. Verifique os campos.' });
      } else {
        setErrors({
          submit: `Erro ao ${
            isEditing ? 'atualizar' : 'cadastrar'
          } dependente. Tente novamente.`,
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      cnpjAssociacao: cnpj,
      idPessoa: pessoa?.id || '',
      cpf: '',
      nome: '',
      dataNasc: '',
      sexo: '',
      grauParentesco: '',
    });
    setErrors({});
    setCpfError('');
    setIsLoading(false);
    onClose();
  };

  if (!show) return null;

  return (
    <div
      className="modal show d-block"
      tabIndex="-1"
      style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
    >
      <div className="modal-dialog modal-lg modal-dialog-centered">
        <div className="modal-content">
          <div
            className="modal-header bg-success text-white"
            style={{ height: '30px' }}
          >
            <h5 className="modal-title">
              <i className="bi bi-person-plus me-2"></i>
              {isEditing ? 'Editar Dependente' : 'Novo Dependente'}
            </h5>
            <button
              type="button"
              className="btn-close btn-close-white"
              onClick={handleClose}
              disabled={isLoading}
            ></button>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="modal-body small">
              {errors.submit && (
                <div className="alert alert-danger">
                  <i className="bi bi-exclamation-triangle me-2"></i>
                  {errors.submit}
                </div>
              )}

              <div className="row g-2 small">
                <div className="col-md-3 input-group-sm">
                  <label className="form-label">
                    CPF Dependente <span className="text-danger">*</span>
                  </label>
                  <CPFMaskInput
                    id="cpf"
                    value={formatCPF(formData.cpf)}
                    onChange={e =>
                      handleInputChange({
                        target: {
                          name: 'cpf',
                          value: unformatCPF(e.target.value),
                        },
                      })
                    }
                    name="cpf"
                    type="text"
                    disabled={isLoading}
                    className={`form-control ${cpfError ? 'is-invalid' : ''}`}
                  />
                  {cpfError && (
                    <div className="invalid-feedback">{cpfError}</div>
                  )}
                </div>

                <div className="col-md-9 input-group-sm">
                  <label className="form-label">
                    Nome do Dependente <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    className={`form-control ${
                      errors.nome ? 'is-invalid' : ''
                    }`}
                    name="nome"
                    value={formData.nome}
                    onChange={handleInputChange}
                    disabled={isLoading}
                    required
                  />
                  {errors.nome && (
                    <div className="invalid-feedback">{errors.nome}</div>
                  )}
                </div>
                <div className="col-md-3 input-group-sm">
                  <label className="form-label">
                    Data de Nascimento <span className="text-danger">*</span>
                  </label>
                  <input
                    type="date"
                    className={`form-control ${
                      errors.dataNasc ? 'is-invalid' : ''
                    }`}
                    name="dataNasc"
                    value={formData.dataNasc}
                    onChange={handleInputChange}
                    disabled={isLoading}
                    required
                  />
                  {errors.dataNasc && (
                    <div className="invalid-feedback">{errors.dataNasc}</div>
                  )}
                </div>
                <div className="col-md-3 input-group-sm">
                  <label className="form-label">Sexo</label>
                  <select
                    className={`form-select ${errors.sexo ? 'is-invalid' : ''}`}
                    name="sexo"
                    value={formData.sexo}
                    onChange={handleInputChange}
                    disabled={isLoading}
                  >
                    <option value="">Selecione o sexo...</option>
                    <option value="MASCULINO">MASCULINO</option>
                    <option value="FEMININO">FEMININO</option>
                  </select>
                  {errors.sexo && (
                    <div className="invalid-feedback">{errors.sexo}</div>
                  )}
                </div>
                <div className="col-md-6 input-group-sm">
                  <label className="form-label">
                    Grau de Parentesco <span className="text-danger">*</span>
                  </label>
                  <select
                    className={`form-select ${
                      errors.grauParentesco ? 'is-invalid' : ''
                    }`}
                    name="grauParentesco"
                    value={formData.grauParentesco}
                    onChange={handleInputChange}
                    disabled={isLoading}
                    required
                  >
                    <option value="">Selecione o grau de parentesco...</option>
                    <option value="CÔNJUGE">CÔNJUGE</option>
                    <option value="COMPANHEIRO(A)">COMPANHEIRO(A)</option>
                    <option value="FILHO(A)">FILHO(A)</option>
                    <option value="ENTEADO(A)">ENTEADO(A)</option>
                    <option value="PAI">PAI</option>
                    <option value="MÃE">MÃE</option>
                    <option value="PADRASTO">PADRASTO</option>
                    <option value="MADRASTA">MADRASTA</option>
                    <option value="IRMÃO/IRMÃ">IRMÃO/IRMÃ</option>
                    <option value="AVÔ/AVÓ">AVÔ/AVÓ</option>
                    <option value="NETO(A)">NETO(A)</option>
                    <option value="GENRO/NORA">GENRO/NORA</option>
                    <option value="SOGRO(A)">SOGRO(A)</option>
                    <option value="CUNHADO(A)">CUNHADO(A)</option>
                    <option value="TIO(A)">TIO(A)</option>
                    <option value="SOBRINHO(A)">SOBRINHO(A)</option>
                    <option value="PRIMO(A)">PRIMO(A)</option>
                  </select>
                  {errors.grauParentesco && (
                    <div className="invalid-feedback">
                      {errors.grauParentesco}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                onClick={handleClose}
                disabled={isLoading}
              >
                <i className="bi bi-x-lg me-1" />
                Cancelar
              </button>
              <button
                type="submit"
                className="btn btn-success btn-sm"
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <span
                      className="spinner-border spinner-border-sm me-2"
                      role="status"
                    />
                    Salvando...
                  </>
                ) : (
                  <>
                    <i className="bi bi-check-lg me-1" />
                    {isEditing ? 'Atualizar' : 'Salvar'}
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default PessoaDependenteModal;
